import * as THREE from 'three'
import type {
  PointCloudOctree,
  PointCloudOctreeNode,
} from '@/plugins/pointCloud/core/PointCloudOctree'
import { PointCloudTree } from '@/plugins/pointCloud/core/PointCloudTree.ts'
import type { VisibilityTextureData } from '@/plugins/pointCloud/core/types'
import { debug, measureTimings } from '@/plugins/pointCloud/loaders/global'
import type { PointCloudMaterial } from '@/plugins/pointCloud/materials/PointCloudMaterial'
import { Shader } from '@/plugins/pointCloud/rendering/webgl/Shader'
import { attributeLocations } from '@/plugins/pointCloud/rendering/webgl/utils'
import { WebGLBuffer } from '@/plugins/pointCloud/rendering/webgl/WebGLBuffer'
import { WebGLTexture } from '@/plugins/pointCloud/rendering/webgl/WebGLTexture'
import { toPointCloudSRGB } from '@/plugins/pointCloud/utils/toPointCloudSRGB'
import {
  ClipPolygonMaxVerticeCount,
  ClipTask,
  ElevationGradientRepeat,
  ORTHOGRAPHIC_EQUIVALENT_FOV_USER_DATA_KEY,
  PointSizeType,
} from '@/shared/constants'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('pointcloud')
const DEFAULT_ORTHOGRAPHIC_EDL_FOV = 60

interface RenderParams {
  material?: PointCloudMaterial
  shadowMaps?: any[]
  viewOverride?: THREE.Matrix4
  transparent?: boolean
  vnTextureNodes?: PointCloudOctreeNode[]
  [key: string]: any
}

/** Potree 原生 WebGL 缓存与句柄的只读内存诊断快照。 */
export interface PotreeRendererMemoryDiagnostics {
  bufferCacheSize: number
  shaderCacheSize: number
  textureCacheSize: number
  trackedVaoCount: number
  trackedVboCount: number
  estimatedVboBytes: number
  estimatedTextureBytes: number
  shaderCacheEntries: number
  shaderUniformBlockCount: number
  vaoCreated: number
  vaoDeleted: number
  liveVaoEstimate: number
  vboCreated: number
  vboDeleted: number
  liveVboEstimate: number
  vboUploadedBytes: number
  vboReleasedBytes: number
  programCreated: number
  programDeleted: number
  shaderCreated: number
  shaderDeleted: number
  uniformBufferCreated: number
  uniformBufferDeleted: number
  textureCreated: number
  textureDeleted: number
}

/**
 * 将 GPS 时间裁切范围归一化为 shader 使用的 [0,1] 区间。
 *
 * @param clipRange - 原始裁切范围，非法时按 [0,1] 处理
 * @param globalRange - 全局时间范围 [min,max]，用于归一化
 * @returns 归一化后的 [0,1] 区间表示
 */
export function normalizeGpsTimeClipRange(
  clipRange: [number, number] | null | undefined,
  globalRange: [number, number]
): [number, number] {
  const [globalMin, globalMax] = globalRange
  const globalRangeSize = globalMax - globalMin

  if (!Number.isFinite(globalMin) || !Number.isFinite(globalMax) || globalRangeSize <= 0) {
    return [0, 1]
  }

  if (
    !Array.isArray(clipRange) ||
    clipRange.length < 2 ||
    !Number.isFinite(clipRange[0]) ||
    !Number.isFinite(clipRange[1]) ||
    clipRange[0] > clipRange[1]
  ) {
    return [0, 1]
  }

  return [
    (clipRange[0] - globalMin) / globalRangeSize,
    (clipRange[1] - globalMin) / globalRangeSize,
  ]
}

export function getOrthographicVisibleSize(camera: THREE.OrthographicCamera): {
  width: number
  height: number
} {
  const safeZoom = Math.max(camera.zoom, 1e-9)
  return {
    width: (camera.right - camera.left) / safeZoom,
    height: (camera.top - camera.bottom) / safeZoom,
  }
}

/**
 * 计算正交 EDL 的透视等价梯度校准系数。
 *
 * @param camera - 正交相机，优先读取 ViewportCamera 写入的语义 FOV
 * @returns 用于正交线性深度编码的正值系数
 */
export function getOrthographicEDLScale(camera: THREE.OrthographicCamera): number {
  const storedFov = camera.userData[ORTHOGRAPHIC_EQUIVALENT_FOV_USER_DATA_KEY]
  const fov =
    typeof storedFov === 'number' && Number.isFinite(storedFov) && storedFov > 0 && storedFov < 180
      ? storedFov
      : DEFAULT_ORTHOGRAPHIC_EDL_FOV
  const fovRadians = THREE.MathUtils.degToRad(fov)

  return (2 * Math.tan(fovRadians / 2)) / Math.LN2
}

/**
 * Potree 原生 WebGL 渲染器，绕过 Three.js 场景图直接绘制点云。
 */
export class PotreeRenderer {
  /**
   * 多边形裁切允许最大顶点数量
   */
  clipPolygonMaxVerticeCount = ClipPolygonMaxVerticeCount
  /** Three.js WebGL 渲染器引用 */
  threeRenderer: THREE.WebGLRenderer
  /** WebGL2 上下文，取自 threeRenderer */
  gl: WebGL2RenderingContext
  /** 几何到 VAO/VBO 封装的缓存 */
  buffers: Map<THREE.BufferGeometry, WebGLBuffer>
  /** 材质到编译 Shader 的缓存 */
  shaders: Map<PointCloudMaterial, Shader>
  /** Three 纹理到 WebGLTexture 封装的缓存 */
  textures: Map<THREE.Texture, WebGLTexture>
  /** TypedArray 构造函数到 GL 枚举的映射 */
  glTypeMapping: Map<
    Float32ArrayConstructor | Uint8ArrayConstructor | Uint16ArrayConstructor,
    GLenum
  >
  /** 帧奇偶切换标记，调试/动画用 */
  toggle: number
  private vaoCreatedCount = 0
  private vaoDeletedCount = 0
  private vboCreatedCount = 0
  private vboDeletedCount = 0
  private vboUploadedBytes = 0
  private vboReleasedBytes = 0
  private programCreatedCount = 0
  private programDeletedCount = 0
  private shaderCreatedCount = 0
  private shaderDeletedCount = 0
  private uniformBufferCreatedCount = 0
  private uniformBufferDeletedCount = 0
  private textureCreatedCount = 0
  private textureDeletedCount = 0
  /** 单色模式传给 Raw Shader 的 sRGB 分量复用缓冲。 */
  private readonly _srgbColor: [number, number, number] = [1, 1, 1]
  constructor(threeRenderer: THREE.WebGLRenderer) {
    this.threeRenderer = threeRenderer
    this.gl = this.threeRenderer.getContext() as WebGL2RenderingContext

    this.buffers = new Map()
    this.shaders = new Map()
    this.textures = new Map()

    this.glTypeMapping = new Map()
    this.glTypeMapping.set(Float32Array, this.gl.FLOAT)
    this.glTypeMapping.set(Uint8Array, this.gl.UNSIGNED_BYTE)
    this.glTypeMapping.set(Uint16Array, this.gl.UNSIGNED_SHORT)

    this.toggle = 0
  }

  /** 获取 Potree 原生 WebGL 资源的诊断计数，不持有新的资源引用。 */
  getMemoryDiagnostics(): PotreeRendererMemoryDiagnostics {
    let trackedVaoCount = 0
    let trackedVboCount = 0
    let estimatedVboBytes = 0
    for (const buffer of this.buffers.values()) {
      if (buffer.vao) {
        trackedVaoCount++
      }
      trackedVboCount += buffer.vbos.size
      for (const vbo of buffer.vbos.values()) {
        estimatedVboBytes += vbo.byteLength
      }
    }

    let shaderCacheEntries = 0
    let shaderUniformBlockCount = 0
    for (const shader of this.shaders.values()) {
      shaderCacheEntries += shader.cache.size
      for (const cached of shader.cache.values()) {
        shaderUniformBlockCount += Object.keys(cached.uniformBlocks).length
      }
    }

    let estimatedTextureBytes = 0
    for (const texture of this.textures.values()) {
      const image = texture.texture.image as
        | { data?: ArrayBufferView; width?: number; height?: number }
        | undefined
      if (image?.data && ArrayBuffer.isView(image.data)) {
        estimatedTextureBytes += image.data.byteLength
      } else if (image?.width && image?.height) {
        estimatedTextureBytes += image.width * image.height * 4
      }
    }

    return {
      bufferCacheSize: this.buffers.size,
      shaderCacheSize: this.shaders.size,
      textureCacheSize: this.textures.size,
      trackedVaoCount,
      trackedVboCount,
      estimatedVboBytes,
      estimatedTextureBytes,
      shaderCacheEntries,
      shaderUniformBlockCount,
      vaoCreated: this.vaoCreatedCount,
      vaoDeleted: this.vaoDeletedCount,
      liveVaoEstimate: this.vaoCreatedCount - this.vaoDeletedCount,
      vboCreated: this.vboCreatedCount,
      vboDeleted: this.vboDeletedCount,
      liveVboEstimate: this.vboCreatedCount - this.vboDeletedCount,
      vboUploadedBytes: this.vboUploadedBytes,
      vboReleasedBytes: this.vboReleasedBytes,
      programCreated: this.programCreatedCount,
      programDeleted: this.programDeletedCount,
      shaderCreated: this.shaderCreatedCount,
      shaderDeleted: this.shaderDeletedCount,
      uniformBufferCreated: this.uniformBufferCreatedCount,
      uniformBufferDeleted: this.uniformBufferDeletedCount,
      textureCreated: this.textureCreatedCount,
      textureDeleted: this.textureDeletedCount,
    }
  }

  /**
   * 删除几何关联的 VAO、VBO 及缓存记录。
   *
   * @param geometry - Three.js BufferGeometry
   */
  deleteBuffer(geometry: THREE.BufferGeometry) {
    const webglBuffer = this.buffers.get(geometry)
    if (webglBuffer != null) {
      if (webglBuffer.disposeHandler) {
        geometry.removeEventListener('dispose', webglBuffer.disposeHandler)
        webglBuffer.disposeHandler = null
      }
      this._deleteWebGLBuffer(webglBuffer)
      this.buffers.delete(geometry)
    }
  }

  /**
   * 为几何创建 VAO 与 VBO，并在 geometry dispose 时自动清理。
   *
   * @param geometry - Three.js BufferGeometry
   * @returns 封装后的 WebGLBuffer 记录
   */
  createBuffer(geometry: THREE.BufferGeometry): WebGLBuffer {
    const gl = this.gl
    const webglBuffer = new WebGLBuffer()

    webglBuffer.vao = gl.createVertexArray()
    if (webglBuffer.vao) {
      this.vaoCreatedCount++
    }
    webglBuffer.numElements = geometry.attributes.position.count

    gl.bindVertexArray(webglBuffer.vao)

    for (const attributeName in geometry.attributes) {
      const bufferAttribute = geometry.attributes[attributeName]

      const vbo = gl.createBuffer()
      if (vbo) {
        this.vboCreatedCount++
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, vbo)
      gl.bufferData(gl.ARRAY_BUFFER, bufferAttribute.array, gl.STATIC_DRAW)
      this.vboUploadedBytes += bufferAttribute.array.byteLength

      const normalized = bufferAttribute.normalized
      const type = this.glTypeMapping.get(bufferAttribute.array.constructor as any) as GLenum

      if (attributeLocations[attributeName] === undefined) {
        //attributeLocation = attributeLocations["aExtra"];
      } else {
        const attributeLocation = attributeLocations[attributeName].location

        gl.vertexAttribPointer(attributeLocation, bufferAttribute.itemSize, type, normalized, 0, 0)
        gl.enableVertexAttribArray(attributeLocation)
      }

      webglBuffer.vbos.set(attributeName, {
        handle: vbo,
        name: attributeName,
        count: bufferAttribute.count,
        itemSize: bufferAttribute.itemSize,
        type: geometry.attributes.position.array.constructor,
        version: 0,
        byteLength: bufferAttribute.array.byteLength,
      })
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, null)

    gl.bindVertexArray(null)

    const disposeHandler = () => {
      this.deleteBuffer(geometry)
    }
    webglBuffer.disposeHandler = disposeHandler
    geometry.addEventListener('dispose', disposeHandler)

    return webglBuffer
  }

  /**
   * 更新几何的 VBO 数据，新增属性时自动创建。
   *
   * @param geometry - Three.js BufferGeometry
   */
  updateBuffer(geometry: THREE.BufferGeometry) {
    const gl = this.gl

    const webglBuffer = this.buffers.get(geometry)

    if (!webglBuffer) {
      return
    }

    gl.bindVertexArray(webglBuffer.vao)

    for (const attributeName in geometry.attributes) {
      const bufferAttribute = geometry.attributes[attributeName]

      const normalized = bufferAttribute.normalized
      const type = this.glTypeMapping.get(bufferAttribute.array.constructor as any) as GLenum

      let vbo: globalThis.WebGLBuffer | null = null
      if (!webglBuffer.vbos.has(attributeName)) {
        vbo = gl.createBuffer()
        if (vbo) {
          this.vboCreatedCount++
        }

        webglBuffer.vbos.set(attributeName, {
          handle: vbo,
          name: attributeName,
          count: bufferAttribute.count,
          itemSize: bufferAttribute.itemSize,
          type: geometry.attributes.position.array.constructor,
          version: (bufferAttribute as any).version,
          byteLength: bufferAttribute.array.byteLength,
        })
      } else {
        const existingVbo = webglBuffer.vbos.get(attributeName)
        if (existingVbo) {
          vbo = existingVbo.handle
          existingVbo.version = (bufferAttribute as any).version
          existingVbo.byteLength = bufferAttribute.array.byteLength
        }
      }

      gl.bindBuffer(gl.ARRAY_BUFFER, vbo)
      gl.bufferData(gl.ARRAY_BUFFER, bufferAttribute.array, gl.STATIC_DRAW)
      this.vboUploadedBytes += bufferAttribute.array.byteLength

      if (attributeLocations[attributeName] === undefined) {
        //attributeLocation = attributeLocations["aExtra"];
      } else {
        const attributeLocation = attributeLocations[attributeName].location

        gl.vertexAttribPointer(attributeLocation, bufferAttribute.itemSize, type, normalized, 0, 0)
        gl.enableVertexAttribArray(attributeLocation)
      }
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, null)

    gl.bindVertexArray(null)
  }

  /**
   * 遍历场景，收集所有可见的 PointCloudTree。
   *
   * @param scene - 场景根节点
   * @returns `{ octrees }`，含遍历到的八叉树数组
   */
  traverse(scene: THREE.Scene) {
    const octrees: any[] = []

    const stack = [scene]
    while (stack.length > 0) {
      const node: THREE.Object3D = stack.pop()!

      if (node instanceof PointCloudTree) {
        octrees.push(node)
        continue
      }

      const visibleChildren = node.children.filter((c) => c.visible)
      stack.push(...(visibleChildren as any))
    }

    const result = {
      octrees: octrees,
    }

    return result
  }

  /**
   * 将一批可见节点以给定 shader 绘制到目标 RT。
   *
   * @param octree - 源八叉树
   * @param nodes - 可见节点列表
   * @param visibilityTextureData - 可见节点纹理数据，为 null 表示跳过
   * @param camera - 活动相机
   * @param target - 输出目标，null 表示默认帧缓冲
   * @param shader - 已就绪的 Shader
   * @param params - 附加渲染参数，含 material/shadowMaps/transparent 等
   */
  renderNodes(
    octree: PointCloudOctree,
    nodes: PointCloudOctreeNode[],
    visibilityTextureData: VisibilityTextureData | null,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    _target: THREE.WebGLRenderTarget | null,
    shader: Shader,
    params: RenderParams
  ) {
    if (measureTimings) performance.mark('renderNodes-start')

    const gl = this.gl

    const material = params.material ? params.material : octree.material
    const shadowMaps = params.shadowMaps == null ? [] : params.shadowMaps
    let view = camera.matrixWorldInverse

    if (params.viewOverride) {
      view = params.viewOverride
    }

    const worldView = new THREE.Matrix4()

    const mat4holder = new Float32Array(16)

    let i = 0
    for (const node of nodes) {
      if (debug.allowedNodes !== undefined) {
        if (!debug.allowedNodes.includes(node.name)) {
          continue
        }
      }

      if (!node.sceneNode) {
        continue
      }
      const geometryNode = node.geometryNode
      const geometry = geometryNode?.geometry
      if (!geometry) {
        log.debug(`Missing geometry, node: ${node.name}`)
        continue
      }
      const world = node.sceneNode.matrixWorld
      worldView.multiplyMatrices(view, world)

      if (visibilityTextureData) {
        const vnStart = visibilityTextureData.offsets.get(node)
        if (vnStart !== undefined) {
          shader.setUniform1f('uVNStart', vnStart)
        }
      }

      const level = node.getLevel()

      if ((node as any).debug) {
        shader.setUniform('uDebug', true)
      } else {
        shader.setUniform('uDebug', false)
      }

      // let isLeaf = false;
      // if(node instanceof PointCloudOctreeNode){
      // 	isLeaf = Object.keys(node.children).length === 0;
      // }else if(node instanceof PointCloudArena4DNode){
      // 	isLeaf = node.geometryNode.isLeaf;
      // }
      // shader.setUniform("uIsLeafNode", isLeaf);

      // let isLeaf = node.children.filter(n => n != null).length === 0;
      // if(!isLeaf){
      // 	continue;
      // }

      // TODO consider passing matrices in an array to avoid uniformMatrix4fv overhead
      const lModel = shader.uniformLocations['modelMatrix']
      if (lModel) {
        mat4holder.set(world.elements)
        gl.uniformMatrix4fv(lModel, false, mat4holder)
      }

      const lModelView = shader.uniformLocations['modelViewMatrix']
      //mat4holder.set(worldView.elements);
      // faster then set in chrome 63
      for (let j = 0; j < 16; j++) {
        mat4holder[j] = worldView.elements[j]
      }
      gl.uniformMatrix4fv(lModelView, false, mat4holder)
      if (material.clipPolygons && material.clipPolygons.length > 0) {
        const clipPolygonVCount: number[] = []
        const worldViewProjMatrices: any[] = []
        const clipTasks: number[] = []
        const clipPolygonGroups: number[] = []
        const clipPolygonAABBs: number[] = [] // AABB 数据：每个多边形4个浮点数 (minX, minY, maxX, maxY)

        for (const clipPolygon of material.clipPolygons) {
          const view = clipPolygon.viewMatrix
          const proj = clipPolygon.projMatrix

          const worldViewProj = proj.clone().multiply(view).multiply(world)

          clipPolygonVCount.push(clipPolygon.markers.length)
          worldViewProjMatrices.push(worldViewProj)
          clipTasks.push(clipPolygon.clipTask)
          clipPolygonGroups.push(clipPolygon.groupId || 0)

          // 收集 AABB 数据
          clipPolygonAABBs.push(
            clipPolygon.aabb.min.x,
            clipPolygon.aabb.min.y,
            clipPolygon.aabb.max.x,
            clipPolygon.aabb.max.y
          )
        }

        const flattenedMatrices = worldViewProjMatrices.flatMap((m) => m.elements)

        const flattenedVertices: number[] = new Array(
          this.clipPolygonMaxVerticeCount * 3 * material.clipPolygons.length
        )
        for (let i = 0; i < material.clipPolygons.length; i++) {
          const clipPolygon = material.clipPolygons[i]
          for (let j = 0; j < clipPolygon.markers.length; j++) {
            // flattenedVertices[i * 24 + (j * 3 + 0)] = clipPolygon.markers[j].position.x;
            // flattenedVertices[i * 24 + (j * 3 + 1)] = clipPolygon.markers[j].position.y;
            // flattenedVertices[i * 24 + (j * 3 + 2)] = clipPolygon.markers[j].position.z;
            flattenedVertices[i * this.clipPolygonMaxVerticeCount * 3 + (j * 3 + 0)] =
              clipPolygon.markers[j].position.x
            flattenedVertices[i * this.clipPolygonMaxVerticeCount * 3 + (j * 3 + 1)] =
              clipPolygon.markers[j].position.y
            flattenedVertices[i * this.clipPolygonMaxVerticeCount * 3 + (j * 3 + 2)] =
              clipPolygon.markers[j].position.z
          }
        }

        const lClipPolygonVCount = shader.uniformLocations['uClipPolygonVCount[0]']
        gl.uniform1iv(lClipPolygonVCount, clipPolygonVCount)

        const uClipPolygonTasks = shader.uniformLocations['uClipPolygonTasks[0]']
        gl.uniform1iv(uClipPolygonTasks, clipTasks)

        const uClipPolygonGroups = shader.uniformLocations['uClipPolygonGroups[0]']
        gl.uniform1iv(uClipPolygonGroups, clipPolygonGroups)

        const uClipPolygonAABBs = shader.uniformLocations['uClipPolygonAABBs[0]']
        gl.uniform4fv(uClipPolygonAABBs, clipPolygonAABBs)

        const lClipPolygonVP = shader.uniformLocations['uClipPolygonWVP[0]']
        gl.uniformMatrix4fv(lClipPolygonVP, false, flattenedMatrices)

        const lClipPolygons = shader.uniformLocations['uClipPolygonVertices[0]']
        gl.uniform3fv(lClipPolygons, flattenedVertices)
      }

      //shader.setUniformMatrix4("modelMatrix", world);
      //shader.setUniformMatrix4("modelViewMatrix", worldView);
      shader.setUniform1f('uLevel', level)
      // @ts-expect-error 旧代码：OctreeGeometryNode 缺少 estimatedSpacing 类型
      shader.setUniform1f('uNodeSpacing', geometryNode.estimatedSpacing)

      shader.setUniform1f('uPCIndex', i)
      // uBBSize

      if (shadowMaps.length > 0) {
        const lShadowMap = shader.uniformLocations['uShadowMap[0]']

        shader.setUniform3f('uShadowColor', material.uniforms.uShadowColor.value)

        const bindingStart = 5
        const bindingPoints = new Array(shadowMaps.length).fill(bindingStart).map((a, i) => a + i)
        gl.uniform1iv(lShadowMap, bindingPoints)

        for (let i = 0; i < shadowMaps.length; i++) {
          const shadowMap = shadowMaps[i]
          const bindingPoint = bindingPoints[i]
          const glTexture = (this.threeRenderer.properties.get(shadowMap.target.texture) as any)
            .__webglTexture

          gl.activeTexture(gl[`TEXTURE${bindingPoint}`])
          gl.bindTexture(gl.TEXTURE_2D, glTexture)
        }

        {
          const worldViewMatrices = shadowMaps
            .map((sm) => sm.camera.matrixWorldInverse)
            .map((view) => new THREE.Matrix4().multiplyMatrices(view, world))

          const flattenedMatrices = worldViewMatrices.flatMap((c) => c.elements)
          const lWorldView = shader.uniformLocations['uShadowWorldView[0]']
          gl.uniformMatrix4fv(lWorldView, false, flattenedMatrices)
        }

        {
          const flattenedMatrices = shadowMaps.flatMap((sm) => sm.camera.projectionMatrix.elements)
          const lProj = shader.uniformLocations['uShadowProj[0]']
          gl.uniformMatrix4fv(lProj, false, flattenedMatrices)
        }
      }

      if (geometry.attributes['gps-time']) {
        const attGPS = octree.getAttribute('gps-time')

        const initialRange = attGPS.initialRange
        const initialRangeSize = initialRange[1] - initialRange[0]

        const globalRange = attGPS.range
        const globalRangeSize = globalRange[1] - globalRange[0]

        let scale = initialRangeSize / globalRangeSize
        let offset = -(globalRange[0] - initialRange[0]) / initialRangeSize

        scale = Number.isNaN(scale) ? 1 : scale
        offset = Number.isNaN(offset) ? 0 : offset

        shader.setUniform1f('uGpsScale', scale)
        shader.setUniform1f('uGpsOffset', offset)
        //shader.setUniform2f("uFilterGPSTimeClipRange", [-Infinity, Infinity]);

        const normalizedClipRange = normalizeGpsTimeClipRange(
          material.uniforms.uFilterGPSTimeClipRange.value,
          globalRange
        )

        shader.setUniform2f('uFilterGPSTimeClipRange', normalizedClipRange)

        // // ranges in full gps coordinate system
        // const globalRange = attGPS.range;
        // const bufferRange = bufferAttribute.potree.range;

        // // ranges in [0, 1]
        // // normalizedGlobalRange = [0, 1]
        // // normalizedBufferRange: norm buffer within norm global range e.g. [0.2, 0.8]
        // const globalWidth = globalRange[1] - globalRange[0];
        // const normalizedBufferRange = [
        // 	(bufferRange[0] - globalRange[0]) / globalWidth,
        // 	(bufferRange[1] - globalRange[0]) / globalWidth,
        // ];

        // shader.setUniform2f("uNormalizedGpsBufferRange", normalizedBufferRange);

        // let uFilterGPSTimeClipRange = material.uniforms.uFilterGPSTimeClipRange.value;
        // let gpsCliPRangeMin = uFilterGPSTimeClipRange[0]
        // let gpsCliPRangeMax = uFilterGPSTimeClipRange[1]
        // shader.setUniform2f("uFilterGPSTimeClipRange", [gpsCliPRangeMin, gpsCliPRangeMax]);

        // shader.setUniform1f("uGpsScale", bufferAttribute.potree.scale);
        // shader.setUniform1f("uGpsOffset", bufferAttribute.potree.offset);
      }

      {
        const uFilterReturnNumberRange = material.uniforms.uFilterReturnNumberRange.value
        const uFilterNumberOfReturnsRange = material.uniforms.uFilterNumberOfReturnsRange.value
        const uFilterPointSourceIDClipRange = material.uniforms.uFilterPointSourceIDClipRange.value

        shader.setUniform2f('uFilterReturnNumberRange', uFilterReturnNumberRange)
        shader.setUniform2f('uFilterNumberOfReturnsRange', uFilterNumberOfReturnsRange)
        shader.setUniform2f('uFilterPointSourceIDClipRange', uFilterPointSourceIDClipRange)
      }

      let webglBuffer: any = null
      if (!this.buffers.has(geometry)) {
        webglBuffer = this.createBuffer(geometry)
        this.buffers.set(geometry, webglBuffer)
      } else {
        webglBuffer = this.buffers.get(geometry)
        for (const attributeName in geometry.attributes) {
          const attribute = geometry.attributes[attributeName]

          const vbo = webglBuffer.vbos.get(attributeName)
          if (vbo && (attribute as any).version > vbo.version) {
            this.updateBuffer(geometry)
          }
        }
      }

      gl.bindVertexArray(webglBuffer.vao)

      const isExtraAttribute =
        attributeLocations[material.activeAttributeName] === undefined &&
        Object.keys(geometry.attributes).includes(material.activeAttributeName)

      if (isExtraAttribute) {
        const attributeLocation = attributeLocations['aExtra'].location

        for (const attributeName in geometry.attributes) {
          const vbo = webglBuffer.vbos.get(attributeName)

          if (vbo) {
            gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle)
          }
          gl.disableVertexAttribArray(attributeLocation)
        }

        const attName = material.activeAttributeName
        const bufferAttribute = geometry.attributes[attName]
        const vbo = webglBuffer.vbos.get(attName)

        if (bufferAttribute !== undefined && vbo !== undefined) {
          const type = this.glTypeMapping.get(bufferAttribute.array.constructor as any) as GLenum
          const normalized = bufferAttribute.normalized

          gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle)
          gl.vertexAttribPointer(
            attributeLocation,
            bufferAttribute.itemSize,
            type,
            normalized,
            0,
            0
          )
          gl.enableVertexAttribArray(attributeLocation)
        }

        {
          // @ts-expect-error 旧代码：pointAttributes 可能为 null
          const attExtra = octree.pcoGeometry.pointAttributes.attributes.find(
            (a) => a.name === attName
          )

          let range = material.getRange(attName)
          if (!range) {
            // @ts-expect-error 旧代码：attExtra 可能为 undefined
            range = attExtra.range
          }

          if (!range) {
            range = [0, 1]
          }

          // @ts-expect-error 旧代码：attExtra 可能为 undefined 且缺少 initialRange 类型
          const initialRange = attExtra.initialRange
          const initialRangeSize = initialRange[1] - initialRange[0]

          const globalRange = range
          const globalRangeSize = globalRange[1] - globalRange[0]

          let scale = initialRangeSize / globalRangeSize
          let offset = -(globalRange[0] - initialRange[0]) / initialRangeSize

          scale = Number.isNaN(scale) ? 1 : scale
          offset = Number.isNaN(offset) ? 0 : offset

          shader.setUniform1f('uExtraScale', scale)
          shader.setUniform1f('uExtraOffset', offset)
        }
      } else {
        for (const attributeName in geometry.attributes) {
          const bufferAttribute = geometry.attributes[attributeName]
          const vbo = webglBuffer.vbos.get(attributeName)

          if (attributeLocations[attributeName] !== undefined && vbo) {
            const attributeLocation = attributeLocations[attributeName].location

            const type = this.glTypeMapping.get(bufferAttribute.array.constructor as any) as GLenum
            const normalized = bufferAttribute.normalized

            gl.bindBuffer(gl.ARRAY_BUFFER, vbo.handle)
            gl.vertexAttribPointer(
              attributeLocation,
              bufferAttribute.itemSize,
              type,
              normalized,
              0,
              0
            )
            gl.enableVertexAttribArray(attributeLocation)
          }
        }
      }

      const numPoints = webglBuffer.numElements

      gl.drawArrays(gl.POINTS, 0, numPoints)

      i++
    }

    gl.bindVertexArray(null)

    if (measureTimings) {
      performance.mark('renderNodes-end')
      performance.measure('render.renderNodes', 'renderNodes-start', 'renderNodes-end')
    }
  }

  /**
   * 渲染一棵八叉树的可见节点，自动管理 shader、可见节点纹理等状态。
   *
   * @param octree - 源八叉树
   * @param nodes - 可见节点列表
   * @param camera - 活动相机
   * @param target - 输出目标，null 表示默认帧缓冲
   * @param params - 附加渲染参数，含 material/shadowMaps/transparent 等
   */
  renderOctree(
    octree: PointCloudOctree,
    nodes: PointCloudOctreeNode[],
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    target: THREE.WebGLRenderTarget | null,
    params: RenderParams = {}
  ) {
    const gl = this.gl

    const material = params.material ? params.material : octree.material
    const shadowMaps = params.shadowMaps == null ? [] : params.shadowMaps
    let view = camera.matrixWorldInverse
    let viewInv = camera.matrixWorld

    if (params.viewOverride) {
      view = params.viewOverride
      viewInv = view.clone().invert()
    }

    const proj = camera.projectionMatrix
    const projInv = proj.clone().invert()
    //let worldView = new THREE.Matrix4();

    let shader: Shader
    let visibilityTextureData: VisibilityTextureData | null = null
    let uVNBufferLength = 2048.0

    let currentTextureBindingPoint = 0

    if (material.pointSizeType >= 0) {
      if (
        material.pointSizeType === PointSizeType.ADAPTIVE ||
        material.activeAttributeName === 'level of detail'
      ) {
        const vnNodes = params.vnTextureNodes != null ? params.vnTextureNodes : nodes
        visibilityTextureData = octree.computeVisibilityTextureData(vnNodes, camera)

        const vnt = material.visibleNodesTexture
        const data = vnt.image.data

        if (visibilityTextureData.data.length > data.length) {
          // 扩容材质
          if (material.updateVisibleNodesTexture) {
            const maxTextureSize = this.threeRenderer.capabilities.maxTextureSize
            const res = material.updateVisibleNodesTexture(data.length, maxTextureSize)
            if (res) {
              material.visibleNodesTexture.image.data.set(visibilityTextureData.data)
              material.visibleNodesTexture.needsUpdate = true
            }
          }
        } else {
          data.set(visibilityTextureData.data)
          vnt.needsUpdate = true
        }
        uVNBufferLength = material.visibleNodesTexture.image.data.length / 4
      }
    }

    //if(material.needsUpdate){
    {
      let [vs, fs] = [material.vertexShader, material.fragmentShader]

      // @ts-expect-error 旧代码：PointCloudMaterial 缺少 snapEnabled/numSnapshots 类型
      const numSnapshots = material.snapEnabled ? material.numSnapshots : 0
      const numClipBoxes = material.clipBoxes?.length ? material.clipBoxes.length : 0
      const numClipSpheres = params.clipSpheres?.length ? params.clipSpheres.length : 0
      const numClipPolygons = material.clipPolygons?.length ? material.clipPolygons.length : 0

      const defines = [
        `#define num_shadowmaps ${shadowMaps.length}`,
        `#define num_snapshots ${numSnapshots}`,
        `#define num_clipboxes ${numClipBoxes}`,
        `#define num_clipspheres ${numClipSpheres}`,
        `#define num_clippolygons ${numClipPolygons}`,
        `#define clip_polygon_max_vertice ${this.clipPolygonMaxVerticeCount}`,
      ]

      if (octree.pcoGeometry.root!.isLoaded()) {
        const attributes = octree.pcoGeometry.root!.geometry.attributes

        if (attributes['gps-time']) {
          defines.push('#define clip_gps_enabled')
        }

        if (attributes['return number']) {
          defines.push('#define clip_return_number_enabled')
        }

        if (attributes['number of returns']) {
          defines.push('#define clip_number_of_returns_enabled')
        }

        if (attributes['source id'] || attributes['point source id']) {
          defines.push('#define clip_point_source_id_enabled')
        }
      }

      const definesString = defines.join('\n')

      const vsVersionIndex = vs.indexOf('#version ')
      const fsVersionIndex = fs.indexOf('#version ')

      if (vsVersionIndex >= 0) {
        vs = vs.replace(/(#version .*)/, `$1\n${definesString}`)
      } else {
        vs = `#version 300 es\n${definesString}\n${vs}`
      }

      if (fsVersionIndex >= 0) {
        fs = fs.replace(/(#version .*)/, `$1\n${definesString}`)
      } else {
        fs = `#version 300 es\n${definesString}\n${fs}`
      }

      if (!this.shaders.has(material)) {
        const s = new Shader(gl, 'pointcloud', vs, fs)
        this._recordShaderResourceCreation(s, 0, 0)
        this.shaders.set(material, s)
      } else {
        const existingShader = this.shaders.get(material)!
        const previousEntries = existingShader.cache.size
        const previousUniformBlocks = this._countShaderUniformBlocks(existingShader)
        existingShader.update(vs, fs)
        this._recordShaderResourceCreation(existingShader, previousEntries, previousUniformBlocks)
      }

      material.needsUpdate = false
    }

    shader = this.shaders.get(material)!

    for (const uniformName of Object.keys(material.uniforms)) {
      const uniform = material.uniforms[uniformName]

      // @ts-expect-error 旧代码：IUniform 缺少 type 属性
      if (uniform.type === 't') {
        const texture = uniform.value

        if (!texture) {
          continue
        }

        if (!this.textures.has(texture)) {
          const webglTexture = new WebGLTexture(gl, texture)
          if (webglTexture.id) {
            this.textureCreatedCount++
          }

          this.textures.set(texture, webglTexture)
        }

        const webGLTexture = this.textures.get(texture)
        if (webGLTexture) {
          webGLTexture.update()
        }
      }
    }

    // biome-ignore lint/correctness/useHookAtTopLevel: gl.useProgram 是 WebGL API，非 React hook
    gl.useProgram(shader.program)

    let transparent = false
    if (params.transparent !== undefined) {
      transparent = params.transparent && material.opacity < 1
    } else {
      transparent = material.opacity < 1
    }

    if (transparent) {
      gl.enable(gl.BLEND)
      // gl.blendFunc(gl.SRC_ALPHA, gl.ONE); // 玻璃效果可以用这个混合函数
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA)
      gl.depthMask(false)
      gl.enable(gl.DEPTH_TEST)
    } else {
      gl.disable(gl.BLEND)
      gl.depthMask(true)
      gl.enable(gl.DEPTH_TEST)
    }

    if (params.blendFunc !== undefined) {
      gl.enable(gl.BLEND)
      // @ts-expect-error
      gl.blendFunc(...params.blendFunc)
    }

    if (params.depthTest !== undefined) {
      if (params.depthTest === true) {
        gl.enable(gl.DEPTH_TEST)
      } else {
        gl.disable(gl.DEPTH_TEST)
      }
    }

    if (params.depthWrite !== undefined) {
      if (params.depthWrite === true) {
        gl.depthMask(true)
      } else {
        gl.depthMask(false)
      }
    }

    {
      // UPDATE UNIFORMS
      shader.setUniformMatrix4('projectionMatrix', proj)
      shader.setUniformMatrix4('viewMatrix', view)
      shader.setUniformMatrix4('uViewInv', viewInv)
      shader.setUniformMatrix4('uProjInv', projInv)

      const screenWidth = target ? target.width : material.screenWidth
      const screenHeight = target ? target.height : material.screenHeight

      shader.setUniform1f('uScreenWidth', screenWidth)
      shader.setUniform1f('uScreenHeight', screenHeight)
      shader.setUniform1f('near', camera.near)
      shader.setUniform1f('far', camera.far)

      if (camera instanceof THREE.OrthographicCamera) {
        const orthoSize = getOrthographicVisibleSize(camera)
        shader.setUniform('uUseOrthographicCamera', true)
        shader.setUniform('uOrthoWidth', orthoSize.width)
        shader.setUniform('uOrthoHeight', orthoSize.height)
        shader.setUniform1f('uEDLOrthoScale', getOrthographicEDLScale(camera))
      } else if (camera instanceof THREE.PerspectiveCamera) {
        shader.setUniform('uUseOrthographicCamera', false)
        shader.setUniform1f('fov', (Math.PI * camera.fov) / 180)
      }

      if (material.clipBoxes.length + material.clipPolygons.length === 0) {
        shader.setUniform1i('clipTask', ClipTask.NONE)
      } else {
        shader.setUniform1i('clipTask', material.clipTask)
      }

      shader.setUniform1i('clipMethod', material.clipMethod)

      if (material.clipBoxes && material.clipBoxes.length > 0) {
        //let flattenedMatrices = [].concat(...material.clipBoxes.map(c => c.inverse.elements));

        //const lClipBoxes = shader.uniformLocations["clipBoxes[0]"];
        //gl.uniformMatrix4fv(lClipBoxes, false, flattenedMatrices);

        const lClipBoxes = shader.uniformLocations['clipBoxes[0]']
        gl.uniformMatrix4fv(lClipBoxes, false, material.uniforms.clipBoxes.value)
      }

      // TODO CLIPSPHERES
      if (params.clipSpheres && params.clipSpheres.length > 0) {
        const clipSpheres = params.clipSpheres

        const matrices: any[] = []
        for (const clipSphere of clipSpheres) {
          //let mScale = new THREE.Matrix4().makeScale(...clipSphere.scale.toArray());
          //let mTranslate = new THREE.Matrix4().makeTranslation(...clipSphere.position.toArray());

          //let clipToWorld = new THREE.Matrix4().multiplyMatrices(mTranslate, mScale);
          const clipToWorld = clipSphere.matrixWorld
          const viewToWorld = camera.matrixWorld
          const worldToClip = clipToWorld.clone().invert()

          const viewToClip = new THREE.Matrix4().multiplyMatrices(worldToClip, viewToWorld)

          matrices.push(viewToClip)
        }

        const flattenedMatrices = matrices.flatMap((matrix) => matrix.elements)

        const lClipSpheres = shader.uniformLocations['uClipSpheres[0]']
        gl.uniformMatrix4fv(lClipSpheres, false, flattenedMatrices)

        //const lClipSpheres = shader.uniformLocations["uClipSpheres[0]"];
        //gl.uniformMatrix4fv(lClipSpheres, false, material.uniforms.clipSpheres.value);
      }

      shader.setUniform1f('size', material.size)
      shader.setUniform1f('maxSize', material.uniforms.maxSize.value)
      shader.setUniform1f('minSize', material.uniforms.minSize.value)

      // uniform float uPCIndex
      shader.setUniform1f('uOctreeSpacing', material.spacing)
      shader.setUniform('uOctreeSize', material.uniforms.octreeSize.value)

      // Raw Shader 不会执行 Three.js 的 linear → sRGB 输出转换，uniform 需预编码。
      shader.setUniform3f('uColor', toPointCloudSRGB(material.color, this._srgbColor))
      //uniform float opacity;
      shader.setUniform1f('uOpacity', material.opacity)

      // 启动z值裁切
      shader.setUniform('uEnableZClip', material.enableZClip)
      shader.setUniform('uZClipRange', material.zClipRange)

      shader.setUniform2f('elevationRange', material.elevationRange)
      shader.setUniform2f('intensityRange', material.intensityRange)
      shader.setUniform2f('activeSamplingRange', material.activeSamplingRange)

      shader.setUniform3f('uIntensity_gbc', [
        material.intensityGamma,
        material.intensityBrightness,
        material.intensityContrast,
      ])

      shader.setUniform3f('uRGB_gbc', [
        material.rgbGamma,
        material.rgbBrightness,
        material.rgbContrast,
      ])

      shader.setUniform1f('uTransition', material.transition)
      shader.setUniform1f('wRGB', material.weightRGB)
      shader.setUniform1f('wIntensity', material.weightIntensity)
      shader.setUniform1f('wElevation', material.weightElevation)
      shader.setUniform1f('wClassification', material.weightClassification)
      shader.setUniform1f('wReturnNumber', material.weightReturnNumber)
      shader.setUniform1f('wSourceID', material.weightSourceID)

      shader.setUniform('backfaceCulling', material.uniforms.backfaceCulling.value)

      const vnWebGLTexture = this.textures.get(material.visibleNodesTexture)
      if (vnWebGLTexture) {
        shader.setUniform1i('visibleNodes', currentTextureBindingPoint)
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint)
        gl.bindTexture(vnWebGLTexture.target, vnWebGLTexture.id)
        currentTextureBindingPoint++
      }

      const gradientTexture = this.textures.get(material.gradientTexture)
      if (gradientTexture) {
        shader.setUniform1i('gradient', currentTextureBindingPoint)
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint)
        gl.bindTexture(gradientTexture.target, gradientTexture.id)

        const repeat = material.elevationGradientRepeat
        if (repeat === ElevationGradientRepeat.REPEAT) {
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.REPEAT)
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.REPEAT)
        } else if (repeat === ElevationGradientRepeat.MIRRORED_REPEAT) {
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT)
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT)
        } else {
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
          gl.texParameteri(gradientTexture.target, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
        }
      }
      currentTextureBindingPoint++

      const classificationTexture = this.textures.get(material.classificationTexture)
      if (classificationTexture) {
        shader.setUniform1i('classificationLUT', currentTextureBindingPoint)
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint)
        gl.bindTexture(classificationTexture.target, classificationTexture.id)
      }
      currentTextureBindingPoint++

      const matcapTexture = this.textures.get(material.matcapTexture)
      if (matcapTexture) {
        shader.setUniform1i('matcapTextureUniform', currentTextureBindingPoint)
        gl.activeTexture(gl.TEXTURE0 + currentTextureBindingPoint)
        gl.bindTexture(matcapTexture.target, matcapTexture.id)
      }
      currentTextureBindingPoint++

      // @ts-expect-error 旧代码：PointCloudMaterial 缺少 snapEnabled 类型
      if (material.snapEnabled === true) {
        {
          const lSnapshot = shader.uniformLocations['uSnapshot[0]']
          const lSnapshotDepth = shader.uniformLocations['uSnapshotDepth[0]']

          const bindingStart = currentTextureBindingPoint
          const lSnapshotBindingPoints = new Array(5).fill(bindingStart).map((a, i) => a + i)
          const lSnapshotDepthBindingPoints = new Array(5)
            .fill(1 + Math.max(...lSnapshotBindingPoints))
            .map((a, i) => a + i)
          currentTextureBindingPoint = 1 + Math.max(...lSnapshotDepthBindingPoints)

          gl.uniform1iv(lSnapshot, lSnapshotBindingPoints)
          gl.uniform1iv(lSnapshotDepth, lSnapshotDepthBindingPoints)

          for (let i = 0; i < 5; i++) {
            const texture = material.uniforms[`uSnapshot`].value[i]
            const textureDepth = material.uniforms[`uSnapshotDepth`].value[i]

            if (!texture) {
              break
            }

            const snapTexture = (this.threeRenderer.properties.get(texture) as any).__webglTexture
            const snapTextureDepth = (this.threeRenderer.properties.get(textureDepth) as any)
              .__webglTexture

            const bindingPoint = lSnapshotBindingPoints[i]
            const depthBindingPoint = lSnapshotDepthBindingPoints[i]

            gl.activeTexture(gl[`TEXTURE${bindingPoint}`])
            gl.bindTexture(gl.TEXTURE_2D, snapTexture)

            gl.activeTexture(gl[`TEXTURE${depthBindingPoint}`])
            gl.bindTexture(gl.TEXTURE_2D, snapTextureDepth)
          }
        }

        {
          const flattenedMatrices = material.uniforms.uSnapView.value.flatMap((c) => c.elements)
          const lSnapView = shader.uniformLocations['uSnapView[0]']
          gl.uniformMatrix4fv(lSnapView, false, flattenedMatrices)
        }
        {
          const flattenedMatrices = material.uniforms.uSnapProj.value.flatMap((c) => c.elements)
          const lSnapProj = shader.uniformLocations['uSnapProj[0]']
          gl.uniformMatrix4fv(lSnapProj, false, flattenedMatrices)
        }
        {
          const flattenedMatrices = material.uniforms.uSnapProjInv.value.flatMap((c) => c.elements)
          const lSnapProjInv = shader.uniformLocations['uSnapProjInv[0]']
          gl.uniformMatrix4fv(lSnapProjInv, false, flattenedMatrices)
        }
        {
          const flattenedMatrices = material.uniforms.uSnapViewInv.value.flatMap((c) => c.elements)
          const lSnapViewInv = shader.uniformLocations['uSnapViewInv[0]']
          gl.uniformMatrix4fv(lSnapViewInv, false, flattenedMatrices)
        }
      }
    }

    shader.setUniform1f('uVNBufferLength', uVNBufferLength)
    this.renderNodes(octree, nodes, visibilityTextureData, camera, target, shader, params)

    gl.activeTexture(gl.TEXTURE2)
    gl.bindTexture(gl.TEXTURE_2D, null)
    gl.activeTexture(gl.TEXTURE0)
  }

  /**
   * 遍历场景并渲染所有可见八叉树到目标 RT。
   *
   * @param scene - 源场景
   * @param camera - 活动相机
   * @param target - 输出目标，null 表示默认帧缓冲
   * @param params - 附加渲染参数，转发至 renderOctree
   */
  render(
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera | THREE.OrthographicCamera,
    target: THREE.WebGLRenderTarget | null = null,
    params: RenderParams = {}
  ) {
    const gl = this.gl

    // PREPARE
    if (target != null) {
      this.threeRenderer.setRenderTarget(target)
    }

    //camera.updateProjectionMatrix();
    // camera.matrixWorldInverse.invert(camera.matrixWorld);

    const traversalResult = this.traverse(scene)

    // RENDER
    for (const octree of traversalResult.octrees) {
      const nodes = octree.visibleNodes
      this.renderOctree(octree, nodes, camera, target, params)
    }

    // CLEANUP
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, null)
    gl.bindBuffer(gl.ARRAY_BUFFER, null)

    gl.bindVertexArray(null)

    this.threeRenderer.resetState()
  }

  private _countShaderUniformBlocks(shader: Shader): number {
    let count = 0
    for (const cached of shader.cache.values()) {
      count += Object.keys(cached.uniformBlocks).length
    }
    return count
  }

  private _deleteWebGLBuffer(webglBuffer: WebGLBuffer): void {
    if (webglBuffer.vao) {
      this.gl.deleteVertexArray(webglBuffer.vao)
      this.vaoDeletedCount++
      webglBuffer.vao = null
    }

    for (const vbo of webglBuffer.vbos.values()) {
      if (vbo.handle) {
        this.gl.deleteBuffer(vbo.handle)
        this.vboDeletedCount++
        this.vboReleasedBytes += vbo.byteLength
        vbo.handle = null
      }
    }
    webglBuffer.vbos.clear()
  }

  private _recordShaderResourceCreation(
    shader: Shader,
    previousEntries: number,
    previousUniformBlocks: number
  ): void {
    const createdPrograms = Math.max(0, shader.cache.size - previousEntries)
    const createdUniformBuffers = Math.max(
      0,
      this._countShaderUniformBlocks(shader) - previousUniformBlocks
    )
    this.programCreatedCount += createdPrograms
    this.shaderCreatedCount += createdPrograms * 2
    this.uniformBufferCreatedCount += createdUniformBuffers
  }

  /** 释放 WebGL 资源：shader、buffer、texture 全量清理。 */
  dispose() {
    // 释放 Shader 资源
    for (const shader of this.shaders.values()) {
      const gl = this.gl
      gl.deleteShader(shader.vs)
      gl.deleteShader(shader.fs)
      gl.deleteProgram(shader.program)
      if (shader.vs) {
        this.shaderDeletedCount++
      }
      if (shader.fs) {
        this.shaderDeletedCount++
      }
      if (shader.program) {
        this.programDeletedCount++
      }

      // 清除缓存
      shader.cache.clear()
    }
    this.shaders.clear()

    // 释放 Buffer 资源
    for (const [geometry, buffer] of this.buffers) {
      if (buffer.disposeHandler) {
        geometry.removeEventListener('dispose', buffer.disposeHandler)
        buffer.disposeHandler = null
      }
      this._deleteWebGLBuffer(buffer)
    }
    this.buffers.clear()

    // 释放 Texture 资源
    for (const texture of this.textures.values()) {
      const gl = this.gl
      gl.deleteTexture(texture.id)
      if (texture.id) {
        this.textureDeletedCount++
      }
    }
    this.textures.clear()
  }
}

export { PotreeRenderer as Renderer }
